import { useRef, useState } from "react";
import { Order, OrderHistory, SearchApiParams } from "../types";
import orderListApi from "../api/orderListApi";
import { Severity } from "../../../type";
import orderHistoryListApi from "../api/orderHistoryListApi";

const limit = 10;

/**
 * 本部用注文商品一覧取得
 * @param showAlert エラー表示
 * @param isAskMode 依頼か履歴か
 */
export default function useOrderList(
  showAlert: (msg: string, severity?: Severity) => void,
  isAskMode: boolean,
) {
  const [total, setTotal] = useState(0); // 在庫のレコードの総数
  const [orderList, setOrderList] = useState<Order[]>([]);
  const [orderHistoryList, setOrderHistoryList] = useState<OrderHistory[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isMoreLoading, setIsMoreLoading] = useState(false);
  const [offset, setOffset] = useState(0);
  const isFastLoading = useRef(false); // 二重読み込み防止
  const [fireMoreLoadEvent, setFireMoreLoadEvent] = useState(false); // スクロール最下層に達した時、値を変更して発火させるイベント
  const [init, setInit] = useState(false);

  const get = async (searchParams: SearchApiParams) => {
    isFastLoading.current = true;
    setIsLoading(true);
    try {
      // offset, limitは必須なのでparamsに付け足す。
      if (isAskMode) {
        const res = await orderListApi({
          ...searchParams,
          ...{
            offset: 0,
            limit,
          },
        });
        const oList = res.orders;
        setTotal(res.total);
        setOrderList(structuredClone(oList));
        setOffset(oList.length);
      } else {
        const res = await orderHistoryListApi({
          ...searchParams,
          ...{
            offset: 0,
            limit,
          },
        });
        const oList = res.orders;
        setTotal(res.total);
        setOrderHistoryList(structuredClone(oList));
        setOffset(oList.length);
      }
    } catch (err) {
      console.error(err);
      showAlert("注文商品一覧情報の取得に失敗しました。", "error");
    } finally {
      isFastLoading.current = false;
      setIsLoading(false);
    }
  };

  const getMore = async (searchParams: SearchApiParams) => {
    if ((isAskMode ? orderList.length >= total : orderHistoryList.length >= total) || !init) {
      isFastLoading.current = false;
      return;
    }
    console.log("orderList.lenght, total:", orderList.length, total);

    isFastLoading.current = true;
    setIsMoreLoading(true);
    try {
      if (isAskMode) {
        const res = await orderListApi({
          limit,
          offset,
          ...searchParams,
        });
        setTotal(res.total);
        setOrderList((prev) => [...prev, ...res.orders]);
        setOffset((prev) => prev + res.orders.length);
      } else {
        const res = await orderHistoryListApi({
          limit,
          offset,
          ...searchParams,
        });
        setTotal(res.total);
        setOrderHistoryList((prev) => [...prev, ...res.orders]);
        setOffset((prev) => prev + res.orders.length);
      }
    } catch (err) {
      console.error(err);
      showAlert("注文商品一覧情報の取得に失敗しました。", "error");
    } finally {
      isFastLoading.current = false;
      setIsMoreLoading(false);
    }
  };

  return [
    {
      total,
      orderList,
      orderHistoryList,
      isLoading,
      isMoreLoading,
      isFastLoading,
      fireMoreLoadEvent,
    },
    { setOrderList, setOrderHistoryList, get, getMore, setFireMoreLoadEvent, setInit, setTotal },
  ] as const;
}
